'use client';
import Heading from '@/src/features/ui/heading';
import ImageWithFallback from '@/src/features/ui/image/image-with-fallback';
import RoundedContainer from '@/src/features/ui/rounded-container';
import { ResponseStoreFrontSmeLoanDetail } from '@/src/interfaces/data-contracts';
import { CurrencyTypeMissing } from '@/src/interfaces/missing-types';
import fmt from '@/src/utils/helpers/number-format';
import PieChart from '@/src/features/shared/pie-chart';

type ContainerProps = {
  withRoundedContainer?: boolean;
  children?: React.ReactNode;
};

const WidgetPieContainer: React.FC<ContainerProps> = ({ withRoundedContainer, children }) => {
  if (withRoundedContainer)
    return (
      <RoundedContainer className="flex-1 p-4 md:p-10" data-testid="summary-left">
        {children}
      </RoundedContainer>
    );
  else return children;
};

type Props = {
  item: ResponseStoreFrontSmeLoanDetail;
  withRoundedContainer?: boolean;
};

export const WidgetPie: React.FC<Props> = ({ item, withRoundedContainer }) => {
  return (
    <WidgetPieContainer withRoundedContainer={withRoundedContainer}>
      <div className="flex flex-row gap-5" data-testid="summary-left">
        <div className="w-[50%] flex-1">
          <div className="relative" data-testid="image-container">
            <div className="absolute top-[-14px] inline-flex h-[66px] w-[122px] rounded-lg border p-4">
              <ImageWithFallback
                data-testid="image"
                src={item.bank?.logoPath}
                alt={item.bank?.name}
                width={122}
                height={66}
              />
            </div>
          </div>
          <div className="mt-[78px] h-[158px] w-[158px] md:h-[200px] md:w-[200px]">
            <PieChart
              data={[item.amount, item.totalInterest, item.expenseAmount]}
              colors={['#46ABF3', '#F6A927', '#2787F6']}
              fill
            />
          </div>
        </div>
        <div className="flex w-[50%] flex-col gap-6 text-left" data-testid="left-info">
          <Heading
            tagType="h1"
            cls="mb-2 flex-1 text-xs font-semibold text-black800 md:text-sm"
            data-testid="product-name"
          >
            {item.name}
          </Heading>
          <div className="flex flex-col gap-2">
            <div className="text-base font-bold text-black1000 md:text-xl" data-testid="product-amount">
              {fmt(item.amount, CurrencyTypeMissing.TRY)}
            </div>
            <div
              className="text-xs text-black600 before:mr-1 before:inline-block before:h-[14px] before:w-[14px] before:rounded before:bg-blue md:text-base md:before:mr-2"
              data-testid="amount-label"
            >
              Anapara
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-base font-bold text-black1000 md:text-xl" data-testid="product-interest">
              {fmt(item.totalInterest, CurrencyTypeMissing.TRY)}
            </div>
            <div
              className="text-xs text-black600 before:mr-1 before:inline-block before:h-[14px] before:w-[14px] before:rounded before:bg-warning md:text-base md:before:mr-2"
              data-testid="product-interest-label"
            >
              {item.bank?.financeType === 2 ? 'Toplam Kâr Payı' : 'Toplam Faiz'}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-base font-bold text-black1000 md:text-xl" data-testid="product-apportionment">
              {fmt(item.expenseAmount, CurrencyTypeMissing.TRY)}
            </div>
            <div
              className="text-xs text-black600 before:mr-1 before:inline-block before:h-[14px] before:w-[14px] before:rounded before:bg-intro md:text-base md:before:mr-2"
              data-testid="product-apportionment-label"
            >
              Tahsis Ücreti
            </div>
          </div>
        </div>
      </div>
    </WidgetPieContainer>
  );
};
